/**
 * Ligne « Solution » de la carte Informations d'une fiche.
 *
 * Le commercial y retient la solution à proposer à cette entreprise ; tant
 * qu'il ne choisit rien, le CRM prend la mieux adaptée à l'activité (choix
 * automatique, affiché). Le discours d'ouverture, « Préparer mon appel » et
 * le copilote s'alignent sur ce choix.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/crmApi";

export default function CompanySolution({ company, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/companies/${company.id}/solutions`); setData(d); }
    catch { setData(null); }
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  const choose = async (id) => {
    setSaving(true);
    try {
      const { data: d } = await api.put(`/companies/${company.id}/solution`, { solution_id: id || null });
      setData(d);
      toast.success(id ? "Solution retenue pour cette fiche" : "Choix automatique rétabli");
      onSaved?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Catalogue vide ou API indisponible : la ligne ne promet rien.
  if (!data || (data.applicable.length === 0 && data.autres.length === 0)) return null;

  const retained = data.retained_id || "";
  const adapted = new Set(data.applicable.map((s) => s.id));
  const offActivity = retained && !adapted.has(retained);

  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 items-baseline py-2 border-b border-slate-50 last:border-0">
      <span className="text-[12.5px] text-slate-400">Solution</span>
      <div className="min-w-0">
        <select data-testid="company-solution" value={retained} disabled={saving}
          onChange={(e) => choose(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[13px] text-slate-700 outline-none focus:border-indigo-500 disabled:opacity-60">
          <option value="">{data.picked ? `Automatique — ${data.picked.nom}` : "Automatique — aucune solution adaptée"}</option>
          {data.applicable.length > 0 && (
            <optgroup label="Adaptées à l'activité">
              {data.applicable.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </optgroup>
          )}
          {data.autres.length > 0 && (
            <optgroup label="Autres solutions">
              {data.autres.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </optgroup>
          )}
        </select>
        {offActivity && (
          <span data-testid="company-solution-warning" className="mt-1 block text-[11.5px] text-amber-700">
            Hors des activités visées par cette solution — le discours s'adaptera quand même.
          </span>
        )}
      </div>
    </div>
  );
}
